import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { SEO } from '../components/SEO';
import { Calendar, Clock, MapPin, Users, CheckCircle2, AlertCircle, Info, XCircle, CreditCard, Banknote, Building2, Copy, ArrowLeft, Package, Eye, Camera } from 'lucide-react';
import { toast } from 'sonner';

// Paket-Definitionen als Array für die Anzeige
const PACKAGES_ARRAY = [
  {
    id: 'grundkurs',
    name: 'BOTTROP: Erste-Hilfe-Kurs (9 UE)',
    price: 35.00,
    priceLabel: '35 €',
    shortDesc: 'Behördlich anerkannter Erste-Hilfe-Kurs für den Führerschein, Arbeit und Ausbildung (Dauer: 7,5 Std.)',
    details: [
      'Für Fahrschüler aller Führerscheinklassen – auch für LKW & BUS',
      'Für Ersthelfer, Betriebe, Unternehmen, Studenten, Auszubildende, Trainer'
    ],
    badge: 'Bundesweit zugelassen',
    icon: Package,
    popular: false,
    paypalButtonId: '3AP3B2ZKV9R6A'
  },
  {
    id: 'sehtest',
    name: 'BOTTROP: Erste-Hilfe + Sehtest',
    price: 37.50,
    priceLabel: '37,50 €',
    shortDesc: 'Behördlich anerkannter Erste-Hilfe-Kurs + amtlicher Sehtest (Auto + Motorrad) (Dauer: 7,5 Std.)',
    details: [
      'Für Fahrschüler aller Führerscheinklassen – auch für LKW & BUS',
      'Für Ersthelfer, Betriebe, Unternehmen, Studenten, Auszubildende, Trainer'
    ],
    badge: 'Bundesweit anerkannt',
    icon: Eye,
    popular: false,
    paypalButtonId: 'JZSUEFEWWLAFA'
  },
  {
    id: 'passbilder',
    name: 'BOTTROP: Erste-Hilfe + 8 Passbilder',
    price: 37.50,
    priceLabel: '37,50 €',
    shortDesc: 'Behördlich anerkannter Erste-Hilfe-Kurs + biometrische Passbilder für den Führerschein (Dauer: 7,5 Std.)',
    details: [
      'Für Fahrschüler aller Führerscheinklassen – auch für LKW & BUS',
      'Für Ersthelfer, Betriebe, Unternehmen, Studenten, Auszubildende, Trainer'
    ],
    badge: 'Bundesweit anerkannt',
    icon: Camera,
    popular: false,
    paypalButtonId: 'LZRPBP7MFJWNL'
  },
  {
    id: 'komplett',
    name: 'BOTTROP: Komplettpaket',
    price: 40.00,
    priceLabel: '40 €',
    shortDesc: 'Behördlich anerkannter Erste-Hilfe-Kurs + amtl. Sehtest + 8 biometrische Passbilder (Dauer: 7,5 Std.)',
    details: [
      'Für Fahrschüler aller Führerscheinklassen – auch für LKW & BUS',
      'Für Ersthelfer, Betriebe, Unternehmen, Studenten, Auszubildende, Trainer'
    ],
    badge: 'Bundesweit anerkannt',
    icon: CheckCircle2,
    popular: false,
    paypalButtonId: '3VW9LNFN7JKZ4'
  },
  {
    id: 'bg',
    name: 'BOTTROP: Erste-Hilfe-Kurs für Betriebe (BG/DGUV)',
    price: 0,
    priceLabel: 'Kostenfrei',
    shortDesc: 'Teilnahme über Berufsgenossenschaft / Unfallkasse.',
    details: [
      'Für betriebliche Ersthelfer nach DGUV-Vorschrift 1',
      'Abrechnung direkt über Ihre Berufsgenossenschaft'
    ],
    badge: 'Bei BG-Kostenübernahme',
    icon: Building2,
    popular: false,
    isBG: true
  }
];

// Paket-Definitionen als Object für Lookup
const PACKAGES = {
  grundkurs: PACKAGES_ARRAY[0],
  sehtest: PACKAGES_ARRAY[1],
  passbilder: PACKAGES_ARRAY[2],
  komplett: PACKAGES_ARRAY[3],
  bg: PACKAGES_ARRAY[4]
};

const BANK_DETAILS = {
  accountHolder: 'Erste Hilfe Germany GbR',
  iban: 'DE22 4245 1220 0000 0473 24',
  bank: 'Stadtsparkasse Bottrop'
};

// Helper-Funktion für Paket-Namen mit roten Hervorhebungen
const formatPackageName = (pkgId, name) => {
  const cleanName = name.replace('BOTTROP: ', '');
  
  switch(pkgId) {
    case 'sehtest':
      // "Erste-Hilfe + Sehtest" → nur "Sehtest" rot
      return <>Erste-Hilfe + <span className="text-red-600">Sehtest</span></>;
    case 'passbilder':
      // "Erste-Hilfe + 8 Passbilder" → nur "8 Passbilder" rot
      return <>Erste-Hilfe + <span className="text-red-600">8 Passbilder</span></>;
    case 'komplett':
      // "Komplettpaket" → nur "Komplettpaket" rot
      return <span className="text-red-600">Komplettpaket</span>;
    case 'bg':
      // BG-Kurs - normaler Name
      return cleanName;
    default:
      return cleanName;
  }
};

const TerminePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const paketParam = searchParams.get('paket');
  const bgParam = searchParams.get('bg'); // BG-Kostenübernahme Parameter
  
  // State für Paketauswahl - kann auch auf der Seite geändert werden
  const [selectedPackageId, setSelectedPackageId] = useState(paketParam || null);
  const selectedPackage = selectedPackageId && PACKAGES[selectedPackageId] ? PACKAGES[selectedPackageId] : null;
  
  // BG-Modus: Wenn bg=true, dann Grundkurs vorauswählen und Preis auf 0 setzen
  const [isBGMode, setIsBGMode] = useState(bgParam === 'true');
  const COURSE_PRICE = isBGMode ? 0 : (selectedPackage ? selectedPackage.price : 40.00);
  
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(bgParam === 'true' ? 'bg' : '');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    privacy: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  const [showPayPal, setShowPayPal] = useState(false);

  // Auto-Scroll nach oben wenn Erfolgsansicht gerendert wird
  useEffect(() => {
    if (isSubmitted) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isSubmitted]);

  // Bei BG-Modus automatisch BG-Paket auswählen
  useEffect(() => {
    if (bgParam === 'true') {
      setSelectedPackageId('bg');
      setIsBGMode(true);
      setPaymentMethod('bg');
      
      // Bei BG-Kurs: Nach kurzem Delay zu Terminauswahl scrollen
      setTimeout(() => {
        const terminSection = document.getElementById('termin-auswahl');
        if (terminSection) {
          terminSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, [bgParam]);

  // Bei initialer Ladung: Wenn BG-Paket via URL-Parameter, dann Zahlungsart vorbelegen
  useEffect(() => {
    if (paketParam === 'bg') {
      setIsBGMode(true);
      setPaymentMethod('bg');
      // Auto-Scroll zu Terminauswahl nach kurzer Verzögerung
      setTimeout(() => {
        const terminSection = document.getElementById('termin-auswahl');
        if (terminSection) {
          terminSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    } else if (paketParam && paketParam !== 'bg') {
      // Selbstzahler-Paket via URL -> Auto-Scroll zu Terminauswahl
      setIsBGMode(false);
      setTimeout(() => {
        const terminSection = document.getElementById('termin-auswahl');
        if (terminSection) {
          terminSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, [paketParam]);

  // Handler für Paketauswahl
  const handlePackageSelect = (packageId) => {
    setSelectedPackageId(packageId);
    
    // BG-Paket ausgewählt -> automatisch BG-Modus aktivieren
    const isBGPackage = packageId === 'bg';
    if (isBGPackage) {
      setIsBGMode(true);
      setPaymentMethod('bg');
    } else {
      // Selbstzahler-Paket -> BG-Modus deaktivieren, Zahlungsart zurücksetzen falls BG war
      setIsBGMode(false);
      if (paymentMethod === 'bg') {
        setPaymentMethod('');
      }
    }
    
    // URL-Parameter aktualisieren ohne Navigation
    const newParams = { paket: packageId };
    if (isBGPackage) newParams.bg = 'true';
    setSearchParams(newParams);
    
    // Auto-Scroll zu Terminauswahl (Punkt 2)
    setTimeout(() => {
      const terminSection = document.getElementById('termin-auswahl');
      if (terminSection) {
        terminSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Handler für BG-Modus umschalten
  const toggleBGMode = (enabled) => {
    setIsBGMode(enabled);
    if (enabled) {
      setPaymentMethod('bg');
      if (!selectedPackageId) {
        setSelectedPackageId('grundkurs');
      }
    } else {
      setPaymentMethod('');
    }
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch dates from backend
  useEffect(() => {
    const fetchDates = async () => {
      try {
        const response = await fetch(`${API_URL}/api/dates`);
        if (response.ok) {
          const data = await response.json();
          setDates(data);
          if (data.length > 0) {
            const firstMonth = data[0].month;
            setSelectedMonth(firstMonth);
          }
        }
      } catch (error) {
        console.error('Error fetching dates:', error);
        toast.error('Fehler beim Laden der Termine');
      } finally {
        setLoading(false);
      }
    };
    fetchDates();
  }, [API_URL]);

  // Group dates by month
  const datesByMonth = useMemo(() => {
    const grouped = {};
    dates.forEach(date => {
      if (!grouped[date.month]) {
        grouped[date.month] = [];
      }
      grouped[date.month].push(date);
    });
    return grouped;
  }, [dates]);

  const months = useMemo(() => Object.keys(datesByMonth), [datesByMonth]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In die Zwischenablage kopiert');
  };

  // Validate form before showing PayPal
  const isFormValid = () => {
    return (
      selectedPackage &&
      selectedDate &&
      selectedDate.spots_left > 0 &&
      formData.firstName &&
      formData.lastName &&
      formData.email &&
      formData.privacy
    );
  };

  // Handle PayPal payment - New API Integration
  const handlePayPalPayment = async () => {
    if (!selectedPackage) {
      toast.error('Bitte wählen Sie zuerst ein Paket aus.');
      return;
    }
    if (!selectedDate) {
      toast.error('Bitte wählen Sie einen Termin aus.');
      return;
    }
    if (!isFormValid()) {
      toast.error('Bitte füllen Sie zuerst alle Pflichtfelder aus und akzeptieren Sie die Datenschutzerklärung.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Step 1: Create registration first (status: pending)
      const regResponse = await fetch(`${API_URL}/api/registrations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone || null,
          course_date: selectedDate.display_date,
          course_time: selectedDate.time,
          course_location: selectedDate.location,
          date_id: selectedDate.id,
          payment_method: 'paypal',
          package_id: selectedPackage?.id || 'komplett',
          package_name: selectedPackage?.name || 'Komplettpaket',
          package_price: COURSE_PRICE,
        }),
      });

      if (!regResponse.ok) {
        const error = await regResponse.json();
        throw new Error(error.detail || 'Anmeldung fehlgeschlagen');
      }

      const registration = await regResponse.json();
      
      // Step 2: Create PayPal order
      const paypalResponse = await fetch(`${API_URL}/api/paypal/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          registration_id: registration.id,
          amount: COURSE_PRICE,
          package_name: selectedPackage?.name || 'Komplettpaket',
        }),
      });

      if (!paypalResponse.ok) {
        const error = await paypalResponse.json();
        throw new Error(error.detail || 'PayPal Fehler');
      }

      const paypalData = await paypalResponse.json();
      
      // Step 3: Redirect to PayPal for payment
      if (paypalData.approve_url) {
        toast.success('Sie werden zu PayPal weitergeleitet...');
        window.location.href = paypalData.approve_url;
      } else {
        throw new Error('PayPal Zahlungslink nicht erhalten');
      }
      
    } catch (error) {
      console.error('PayPal payment error:', error);
      toast.error(error.message || 'Fehler bei der PayPal-Zahlung');
      setIsSubmitting(false);
    }
  };

  // Legacy function kept for compatibility
  const handlePayPalSelect = () => {
    handlePayPalPayment();
  };

  // Create registration after successful PayPal payment (legacy - kept for compatibility)
  const createRegistration = async (paypalOrderId) => {
    // This function is no longer used with the new flow
    // Payment is now handled via redirect and capture on success page
    console.log('Legacy createRegistration called - using new flow instead');
  };

  // Handle non-PayPal submissions
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPackage) {
      toast.error('Bitte wählen Sie zuerst ein Paket aus.');
      return;
    }
    
    if (!selectedDate) {
      toast.error('Bitte wählen Sie einen Termin aus.');
      return;
    }

    if (selectedDate.spots_left <= 0) {
      toast.error('Dieser Termin ist leider ausgebucht.');
      return;
    }

    if (!paymentMethod) {
      toast.error('Bitte wählen Sie eine Zahlungsart aus.');
      return;
    }

    if (!formData.privacy) {
      toast.error('Bitte akzeptieren Sie die Datenschutzerklärung.');
      return;
    }

    // PayPal is handled separately
    if (paymentMethod === 'paypal') {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/registrations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone || null,
          course_date: selectedDate.display_date,
          course_time: selectedDate.time,
          course_location: selectedDate.location,
          date_id: selectedDate.id,
          payment_method: paymentMethod,
          package_id: selectedPackage?.id || 'komplett',
          package_name: selectedPackage?.name || 'Komplettpaket',
          package_price: COURSE_PRICE,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Anmeldung fehlgeschlagen');
      }

      const result = await response.json();
      setRegistrationResult(result);

      setDates(prevDates => 
        prevDates.map(d => 
          d.id === selectedDate.id 
            ? { ...d, spots_left: Math.max(0, d.spots_left - 1) }
            : d
        )
      );

      setIsSubmitted(true);
      toast.success('Anmeldung erfolgreich!');
      
      // Weiterleitung zur Erfolgsseite für Google Ads Conversion Tracking
      const params = new URLSearchParams({
        name: `${formData.firstName} ${formData.lastName}`,
        kurs: selectedPackage?.name?.replace('BOTTROP: ', '') || 'Erste Hilfe Kurs',
        datum: selectedDate?.display_date || '',
        uhrzeit: selectedDate?.time || '09:00 - 16:30 Uhr',
        betrag: selectedPackage?.price?.toFixed(2) || '40.00',
        zahlungsart: formData.paymentMethod === 'paypal' ? 'PayPal' : 
                     formData.paymentMethod === 'cash' ? 'Barzahlung vor Ort' : 
                     formData.paymentMethod === 'bank' ? 'Überweisung' : 'BG-Kostenübernahme'
      });
      
      // Nach kurzer Verzögerung zur Erfolgsseite weiterleiten (für bessere UX)
      setTimeout(() => {
        window.location.href = `/anmeldung-erfolgreich?${params.toString()}`;
      }, 1500);
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.message || 'Ein Fehler ist aufgetreten.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get availability status badge
  const getAvailabilityBadge = (spotsLeft) => {
    if (spotsLeft <= 0) {
      return (
        <div className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <XCircle className="w-3.5 h-3.5" />
          Ausgebucht
        </div>
      );
    } else if (spotsLeft <= 5) {
      return (
        <div className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
          <AlertCircle className="w-3.5 h-3.5" />
          Nur noch {spotsLeft} Plätze
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <Users className="w-3.5 h-3.5" />
          {spotsLeft} freie Plätze
        </div>
      );
    }
  };

  // Success page after registration
  if (isSubmitted && registrationResult) {
    const paymentReference = `Erste-Hilfe-Kurs – ${selectedDate?.display_date} – ${formData.lastName}`;
    
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <SEO page="termine" />
        <Navbar />
        <main className="flex-1">
          {/* MOBILE HERO für Bestätigungsseite - ohne doppelten Titel */}
          <section className="md:hidden">
            <div className="relative w-full aspect-video">
              <img 
                src="https://customer-assets.emergentagent.com/job_firstaid-bottrop/artifacts/wxx53ip7_1D287D4C-BF2F-4A99-BC9A-8A12AAC7CEC2.png"
                alt="Termine & Anmeldung"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="bg-[#1e3a5f] py-4 px-6 text-center">
              <p className="text-sm text-white/90">
                Einfach anmelden · flexibel teilnehmen
              </p>
            </div>
          </section>

          {/* DESKTOP HERO für Bestätigungsseite */}
          <section className="hidden md:block relative h-[40vh] lg:h-[45vh] overflow-hidden">
            <img 
              src="https://customer-assets.emergentagent.com/job_firstaid-bottrop/artifacts/wxx53ip7_1D287D4C-BF2F-4A99-BC9A-8A12AAC7CEC2.png"
              alt="Termine & Anmeldung"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40" />
            <div className="relative h-full flex flex-col items-center justify-center text-center px-4 sm:px-6 lg:px-8">
              <h1 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white leading-tight mb-2 max-w-4xl">
                Termine & Anmeldung
              </h1>
              <p className="text-base sm:text-lg text-white/90 mb-6">
                Einfach anmelden · flexibel teilnehmen
              </p>
            </div>
          </section>

          <div className="py-16">
            <div className="max-w-2xl mx-auto px-4">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-3xl font-bold text-[#1e3a5f] mb-4">Anmeldung erfolgreich!</h2>
                <p className="text-slate-600">
                  Vielen Dank für Ihre Anmeldung. Sie erhalten in Kürze eine Bestätigung per E-Mail.
                </p>
              </div>

              <Card className="bg-slate-50 border-0 mb-6">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-[#1e3a5f] mb-4">Ihre Buchung</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-500">Name:</span> <span className="text-slate-900">{formData.firstName} {formData.lastName}</span></p>
                    <p><span className="text-slate-500">E-Mail:</span> <span className="text-slate-900">{formData.email}</span></p>
                    <p><span className="text-slate-500">Paket:</span> <span className="text-slate-900 font-medium">{selectedPackage?.name || 'Komplettpaket'}</span></p>
                    <p><span className="text-slate-500">Termin:</span> <span className="text-slate-900">{selectedDate?.display_date}</span></p>
                    <p><span className="text-slate-500">Uhrzeit:</span> <span className="text-slate-900">{selectedDate?.time}</span></p>
                    <p><span className="text-slate-500">Ort:</span> <span className="text-slate-900">{selectedDate?.location}</span></p>
                    <p><span className="text-slate-500">Betrag:</span> <span className="text-slate-900 font-semibold">
                      {paymentMethod === 'bg' ? '0,00 € (BG-Kostenübernahme)' : `${COURSE_PRICE.toFixed(2).replace('.', ',')} €`}
                    </span></p>
                    <p><span className="text-slate-500">Zahlungsstatus:</span> 
                      <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                        paymentMethod === 'paypal' || paymentMethod === 'bg' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {paymentMethod === 'paypal' ? 'Bezahlt' : (paymentMethod === 'bg' ? 'BG-Abrechnung' : 'Offen')}
                      </span>
                    </p>
                  </div>
                </CardContent>
              </Card>

              {paymentMethod === 'cash' && (
                <Card className="border-amber-200 bg-amber-50 mb-6">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-amber-900 mb-4 flex items-center gap-2">
                      <Banknote className="w-5 h-5" />
                      Zahlungsinformationen – Barzahlung
                    </h3>
                    <p className="text-amber-800 text-sm">
                      Die Kursgebühr von <strong>{selectedPackage ? selectedPackage.price.toFixed(2).replace('.', ',') : '40,00'} €</strong> ist am Kurstag vor Ort in bar zu bezahlen.
                    </p>
                    <p className="text-amber-700 text-xs mt-3">
                      Bitte bringen Sie den Betrag passend mit.
                    </p>
                  </CardContent>
                </Card>
              )}

              {paymentMethod === 'bg' && (
                <Card className="border-green-300 bg-green-50 mb-6">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-green-900 mb-4 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5" />
                      BG-Kostenübernahme – Keine Zahlung erforderlich
                    </h3>
                    <p className="text-green-800 text-sm mb-3">
                      Die Kursgebühr wird direkt mit Ihrer Berufsgenossenschaft abgerechnet.
                    </p>
                    <div className="bg-white rounded-lg p-4 border border-green-200">
                      <p className="font-medium text-green-800 text-sm mb-2">📋 Bitte zum Kurstag mitbringen:</p>
                      <p className="text-green-700 text-sm">
                        Das <strong>ausgefüllte, gestempelte und unterschriebene DGUV-Abrechnungsformular</strong> Ihrer Berufsgenossenschaft.
                      </p>
                      <a 
                        href="/betriebliche-erste-hilfe" 
                        className="inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-800 underline mt-3"
                      >
                        <Info className="w-4 h-4" />
                        Formular-Download & Infos zu Sonderfällen (BGW, BGN, etc.)
                      </a>
                    </div>
                  </CardContent>
                </Card>
              )}

              {paymentMethod === 'paypal' && (
                <Card className="border-green-200 bg-green-50 mb-6">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-green-900 mb-4 flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      Zahlung erfolgreich – PayPal
                    </h3>
                    <p className="text-green-800 text-sm">
                      Ihre Zahlung von <strong>{COURSE_PRICE.toFixed(2).replace('.', ',')} €</strong> wurde erfolgreich über PayPal abgewickelt.
                    </p>
                    <p className="text-green-700 text-xs mt-3">
                      Sie erhalten eine Bestätigung per E-Mail.
                    </p>
                  </CardContent>
                </Card>
              )}

              <Button 
                className="w-full bg-red-600 hover:bg-red-700"
                onClick={() => {
                  // Vollständiger Reset für neue Anmeldung
                  setIsSubmitted(false);
                  setSelectedDate(null);
                  setSelectedPackageId(null); // Paket zurücksetzen
                  setIsBGMode(false); // BG-Modus zurücksetzen
                  setPaymentMethod('');
                  setShowPayPal(false);
                  setRegistrationResult(null);
                  setFormData({ firstName: '', lastName: '', email: '', phone: '', privacy: false });
                  // URL-Parameter entfernen für sauberen Neustart
                  setSearchParams({});
                  // Nach oben scrollen
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Weiteren Teilnehmer anmelden
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
      <div className="min-h-screen bg-white flex flex-col">
        <SEO page="termine" />
        <Navbar />
        <main className="flex-1">
          
          {/* MOBILE: Klassischer Hero wie Dortmund (Bild oben, Titel darunter) */}
          <div className="md:hidden">
            {/* Vollbreites Bild */}
            <div className="w-full">
              <img 
                src="https://customer-assets.emergentagent.com/job_firstaid-bottrop/artifacts/wxx53ip7_1D287D4C-BF2F-4A99-BC9A-8A12AAC7CEC2.png"
                alt="Termine & Anmeldung"
                className="w-full h-auto object-cover"
                style={{ aspectRatio: '16/9' }}
              />
            </div>
            
            {/* Titel-Bereich unter dem Bild - wie Dortmund */}
            <div className="bg-white px-4 py-6">
              <h1 className="text-2xl font-bold text-[#1e3a5f] leading-tight mb-3" data-testid="page-title-mobile">
                Termine & Anmeldung
              </h1>
              <p className="text-slate-600 text-base leading-relaxed">
                Wählen Sie Ihr Paket und buchen Sie Ihren Wunschtermin – schnell und unkompliziert.
              </p>
            </div>
          </div>

          {/* DESKTOP: Bestehender Hero mit Gradient-Overlay (unverändert) */}
          <div className="hidden md:block">
            <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
              <div 
                className="relative overflow-hidden rounded-xl"
                style={{ 
                  backgroundImage: `url("https://customer-assets.emergentagent.com/job_firstaid-bottrop/artifacts/wxx53ip7_1D287D4C-BF2F-4A99-BC9A-8A12AAC7CEC2.png")`,
                  backgroundSize: '100% 100%',
                  backgroundPosition: 'center center'
                }}
              >
                {/* Gradient Overlay - NUR links für Textbereich */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/90 via-[#1e3a5f]/70 to-[#1e3a5f]/20" />
                
                {/* Content - Einheitliche Höhe */}
                <div className="relative px-8 lg:px-10 py-12 lg:py-14">
                  <div className="flex flex-row items-center justify-between gap-6 min-h-[240px]">
                    {/* Titel + Zusatzinfo - Links */}
                    <div className="max-w-xl">
                      <div className="flex flex-wrap items-center gap-2 mb-4">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white rounded-full text-sm font-semibold shadow-sm">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Samstags & Sonntags
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 text-[#1e3a5f] rounded-full text-sm font-semibold shadow-sm">
                          Flexibel buchen
                        </span>
                      </div>
                      
                      <h1 className="text-3xl lg:text-4xl font-bold text-white leading-tight drop-shadow-sm">
                        Termine & Anmeldung
                      </h1>
                      
                      <p className="text-white/90 text-lg mt-3 drop-shadow-sm">
                        Wählen Sie Ihr Paket und buchen Sie Ihren Wunschtermin – schnell und unkompliziert.
                      </p>
                      
                      {/* Zusätzlicher Teaser-Text */}
                      <div className="mt-6 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                        <p className="text-white font-semibold text-lg">
                          Kurse jeden Samstag & Sonntag von 09:00 – 16:30 Uhr
                        </p>
                        <p className="text-white/80 text-sm mt-1">
                          Bescheinigung erhalten Sie direkt vor Ort – bundesweit gültig.
                        </p>
                      </div>
                    </div>
                    
                    {/* Info-Box - Rechts, vertikal zentriert */}
                    <div className="flex-shrink-0 flex items-center">
                      <div className="bg-white/95 backdrop-blur-sm rounded-xl p-5 shadow-xl">
                        <div className="flex items-center gap-3 mb-3">
                          <Calendar className="w-6 h-6 text-red-600" />
                          <span className="font-semibold text-[#1e3a5f]">Nächste Termine</span>
                        </div>
                        <p className="text-sm text-slate-600 mb-4">Scrollen Sie nach unten für alle verfügbaren Termine</p>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Clock className="w-4 h-4" />
                          <span>7,5 Stunden inkl. Pausen</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <section className="py-16 overflow-x-hidden">
            <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8 overflow-hidden">
              
              {/* SCHRITT 1: PAKET AUSWÄHLEN */}
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-lg">
                    1
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f]">Paket auswählen</h2>
                </div>
                
                {/* Paket-Kacheln - 2x2 Grid mit detaillierten Beschreibungen */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {PACKAGES_ARRAY.filter(pkg => !pkg.isBG).map((pkg) => {
                    const IconComponent = pkg.icon;
                    const isSelected = selectedPackageId === pkg.id;
                    
                    return (
                      <div 
                        key={pkg.id}
                        className={`relative cursor-pointer transition-all duration-200 rounded-xl p-4 sm:p-5 ${
                          isSelected 
                            ? 'bg-red-50 border-2 border-red-500 shadow-lg' 
                            : 'bg-white border-2 border-slate-200 hover:border-red-300 hover:shadow-md'
                        }`}
                        onClick={() => { handlePackageSelect(pkg.id); setIsBGMode(false); setPaymentMethod(''); }}
                        data-testid={`package-${pkg.id}`}
                      >
                        {pkg.popular && (
                          <div className="absolute -top-2.5 left-4 bg-red-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                            Sonderpreis
                          </div>
                        )}
                        
                        <div className="flex gap-4">
                          {/* Selection Indicator + Icon */}
                          <div className="flex flex-col items-center gap-2">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              isSelected ? 'border-red-500 bg-red-500' : 'border-slate-300'
                            }`}>
                              {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                            </div>
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${
                              isSelected ? 'bg-red-100' : 'bg-slate-100'
                            }`}>
                              <IconComponent className={`w-5 h-5 sm:w-6 sm:h-6 ${isSelected ? 'text-red-600' : 'text-[#1e3a5f]'}`} />
                            </div>
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            {/* Paket Name + Preis */}
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <h3 className="font-bold text-[#1e3a5f] text-sm sm:text-base leading-tight flex-1 min-w-0">
                                {formatPackageName(pkg.id, pkg.name)}
                              </h3>
                              <p className={`text-lg sm:text-xl font-bold whitespace-nowrap flex-shrink-0 ${isSelected ? 'text-red-600' : 'text-[#1e3a5f]'}`}>
                                {pkg.price.toFixed(2).replace('.', ',')} €
                              </p>
                            </div>
                            
                            {/* Kurzbeschreibung */}
                            <p className="text-xs sm:text-sm text-slate-600 mb-2">
                              {pkg.shortDesc}
                            </p>
                            
                            {/* Badge + Details */}
                            <p className="text-xs font-semibold text-[#1e3a5f] mb-1">
                              {pkg.badge}:
                            </p>
                            <ul className="space-y-0.5">
                              {pkg.details.map((detail, index) => (
                                <li key={index} className="flex items-start gap-1.5 text-xs text-slate-500">
                                  <span className="text-red-600">•</span>
                                  <span>{detail}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* BG-Paket - identisches Layout wie andere Pakete */}
                {PACKAGES_ARRAY.filter(pkg => pkg.isBG).map((pkg) => {
                  const IconComponent = pkg.icon;
                  const isSelected = selectedPackageId === pkg.id;
                  
                  return (
                    <div 
                      key={pkg.id}
                      className={`relative cursor-pointer transition-all duration-200 rounded-xl p-4 sm:p-5 mt-4 ${
                        isSelected 
                          ? 'bg-red-50 border-2 border-red-500 shadow-lg' 
                          : 'bg-white border-2 border-slate-200 hover:border-red-300 hover:shadow-md'
                      }`}
                      onClick={() => { handlePackageSelect(pkg.id); setIsBGMode(true); setPaymentMethod('bg'); }}
                      data-testid={`package-${pkg.id}`}
                    >
                      <div className="absolute -top-2.5 left-4 bg-red-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                        {pkg.badge}
                      </div>
                      
                      <div className="flex gap-4">
                        {/* Selection Indicator + Icon */}
                        <div className="flex flex-col items-center gap-2">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            isSelected ? 'border-red-500 bg-red-500' : 'border-slate-300'
                          }`}>
                            {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                          </div>
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${
                            isSelected ? 'bg-red-100' : 'bg-slate-100'
                          }`}>
                            <IconComponent className={`w-5 h-5 sm:w-6 sm:h-6 ${isSelected ? 'text-red-600' : 'text-[#1e3a5f]'}`} />
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          {/* Paket Name + Preis */}
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h3 className="font-bold text-[#1e3a5f] text-sm sm:text-base leading-tight flex-1 min-w-0">
                              {formatPackageName(pkg.id, pkg.name)}
                            </h3>
                            <p className={`text-lg sm:text-xl font-bold whitespace-nowrap flex-shrink-0 ${isSelected ? 'text-red-600' : 'text-[#1e3a5f]'}`}>
                              {pkg.priceLabel}
                            </p>
                          </div>
                          
                          {/* Kurzbeschreibung */}
                          <p className="text-xs sm:text-sm text-slate-600 mb-2">
                            {pkg.shortDesc}
                          </p>
                          
                          {/* Badge + Details */}
                          <p className="text-xs font-semibold text-[#1e3a5f] mb-1">
                            Inklusive:
                          </p>
                          <ul className="space-y-0.5">
                            {pkg.details.map((detail, index) => (
                              <li key={index} className="flex items-start gap-1.5 text-xs text-slate-500">
                                <span className="text-red-600">•</span>
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Ausgewähltes Paket Bestätigung */}
                {selectedPackage && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-green-800 font-medium">
                      Gewählt: <span className="font-bold">{selectedPackage.name}</span> – {selectedPackage.isBG ? selectedPackage.priceLabel : `${selectedPackage.price.toFixed(2).replace('.', ',')} €`}
                    </span>
                  </div>
                )}
                
                {/* Hinweis wenn kein Paket gewählt */}
                {!selectedPackage && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span className="text-amber-800 text-sm">
                      Bitte wählen Sie oben ein Paket aus, um einen Termin zu buchen.
                    </span>
                  </div>
                )}
              </div>

              {/* SCHRITT 2: TERMIN AUSWÄHLEN - IMMER SICHTBAR */}
              <div id="termin-auswahl" className="flex items-center gap-3 mb-6 scroll-mt-24">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                  selectedPackage ? 'bg-red-600 text-white' : 'bg-slate-300 text-slate-600'
                }`}>
                  2
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f]">Termin auswählen</h2>
              </div>
              
              <p className="text-lg text-slate-600 max-w-2xl mb-8">
                Wählen Sie Ihren gewünschten Kurstermin aus den verfügbaren Terminen.
              </p>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a5f] mx-auto"></div>
                  <p className="mt-4 text-slate-600">Termine werden geladen...</p>
                </div>
              ) : (
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                  {/* Available Dates */}
                  <div className="w-full overflow-hidden">
                    <h3 className="text-xl font-semibold text-[#1e3a5f] mb-6">Verfügbare Termine</h3>
                    
                    {/* Monats-Buttons - Grid für bessere Mobile-Darstellung */}
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-6">
                      {months.map((month) => (
                        <button
                          key={month}
                          onClick={() => setSelectedMonth(month)}
                          className={`px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                            selectedMonth === month
                              ? 'bg-[#1e3a5f] text-white'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          {month}
                        </button>
                      ))}
                    </div>

                    {/* Termine - KEIN interner Scroll, volle Breite */}
                    <div className="space-y-3">
                      {selectedMonth && datesByMonth[selectedMonth]?.map((date) => {
                        const isBooked = date.spots_left <= 0;
                        const isSelected = selectedDate?.id === date.id;
                        
                        const handleDateClick = () => {
                          if (isBooked) return;
                          if (!selectedPackage) {
                            toast.warning('Bitte wählen Sie zuerst oben ein Paket aus.', {
                              description: 'Scrollen Sie nach oben und wählen Sie ein Kurs-Paket.',
                              duration: 4000,
                            });
                            // Scroll to package selection
                            document.querySelector('[data-testid="package-grundkurs"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            return;
                          }
                          setSelectedDate(date);
                          
                          // Auto-Scroll zum Anmeldeformular (Punkt 3)
                          setTimeout(() => {
                            const formSection = document.getElementById('anmelde-formular');
                            if (formSection) {
                              formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }, 100);
                        };
                        
                        return (
                          <Card 
                            key={date.id}
                            className={`border-2 transition-all w-full ${
                              isBooked
                                ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                                : isSelected
                                  ? 'border-red-500 bg-red-50/50 shadow-md cursor-pointer'
                                  : 'border-slate-200 hover:border-slate-300 hover:shadow-sm cursor-pointer'
                            }`}
                            onClick={handleDateClick}
                          >
                            <CardContent className="p-3 sm:p-4">
                              <div className="flex items-start justify-between gap-2 sm:gap-3">
                                <div className="space-y-1.5 flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-[#1e3a5f] flex-shrink-0" />
                                    <span className={`font-semibold text-sm ${isBooked ? 'text-slate-500' : 'text-[#1e3a5f]'}`}>
                                      {date.display_date}
                                    </span>
                                  </div>
                                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs text-slate-600">
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3.5 h-3.5 flex-shrink-0" />{date.time}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <MapPin className="w-3.5 h-3.5 flex-shrink-0" />{date.location}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex-shrink-0">
                                  {getAvailabilityBadge(date.spots_left)}
                                </div>
                              </div>
                              {isSelected && !isBooked && (
                                <div className="mt-2.5 pt-2.5 border-t border-red-200">
                                  <span className="text-xs sm:text-sm text-red-600 font-medium flex items-center gap-1">
                                    <CheckCircle2 className="w-4 h-4" /> Ausgewählt
                                  </span>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>

                    {selectedMonth && (
                      <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">
                          <span className="font-medium text-[#1e3a5f]">{datesByMonth[selectedMonth]?.length} Termine</span> im {selectedMonth} · 
                          <span className="text-green-600 ml-1">
                            {datesByMonth[selectedMonth]?.filter(d => d.spots_left > 0).length} verfügbar
                          </span>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Registration Form */}
                  <div id="anmelde-formular">
                    <Card className="border-slate-200 sticky top-4">
                      <CardHeader>
                        <CardTitle className="text-xl text-[#1e3a5f]">Anmeldeformular</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {!selectedDate && (
                          <div className="flex items-center gap-3 bg-amber-50 text-amber-700 p-4 rounded-lg mb-6">
                            <AlertCircle className="w-5 h-5 flex-shrink-0" />
                            <span className="text-sm">Bitte wählen Sie zuerst einen Termin aus.</span>
                          </div>
                        )}

                        {selectedDate && selectedDate.spots_left <= 0 && (
                          <div className="flex items-center gap-3 bg-red-50 text-red-700 p-4 rounded-lg mb-6">
                            <XCircle className="w-5 h-5 flex-shrink-0" />
                            <span className="text-sm">Dieser Termin ist leider ausgebucht.</span>
                          </div>
                        )}
                        
                        <form onSubmit={handleSubmit} className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="firstName">Vorname *</Label>
                              <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} required placeholder="Max" />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="lastName">Nachname *</Label>
                              <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} required placeholder="Mustermann" />
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="email">E-Mail-Adresse *</Label>
                            <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required placeholder="max@beispiel.de" />
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="phone">Telefonnummer</Label>
                            <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} placeholder="+49 123 456789" />
                          </div>

                          {selectedDate && selectedDate.spots_left > 0 && (
                            <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
                              <p className="text-sm text-green-700 mb-1 font-medium">Ausgewählter Termin:</p>
                              <p className="font-semibold text-[#1e3a5f] text-sm">{selectedDate.display_date}</p>
                              <p className="text-xs text-slate-600">{selectedDate.time} • {selectedDate.location}</p>
                            </div>
                          )}

                          {/* DSGVO Checkbox */}
                          <div className="flex items-start gap-3 pt-2">
                            <Checkbox 
                              id="privacy" 
                              name="privacy"
                              checked={formData.privacy}
                              onCheckedChange={(checked) => setFormData({...formData, privacy: checked})}
                              className="mt-1"
                            />
                            <Label htmlFor="privacy" className="text-sm text-slate-600 font-normal cursor-pointer">
                              Ich habe die <a href="/datenschutz" className="text-red-600 hover:underline">Datenschutzerklärung</a> gelesen 
                              und bin mit der Verarbeitung meiner Daten einverstanden. *
                            </Label>
                          </div>

                          {/* Zusammenfassung vor Zahlung */}
                          {selectedPackage && selectedDate && formData.firstName && formData.lastName && formData.email && formData.privacy && (
                            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                              <h4 className="font-semibold text-[#1e3a5f] mb-3">Ihre Buchung:</h4>
                              <div className="space-y-1 text-sm text-slate-600 mb-3">
                                <p><span className="font-medium">Paket:</span> {selectedPackage.name}</p>
                                <p><span className="font-medium">Termin:</span> {selectedDate.display_date}, {selectedDate.time}</p>
                                <p><span className="font-medium">Teilnehmer:</span> {formData.firstName} {formData.lastName}</p>
                                <p><span className="font-medium">E-Mail:</span> {formData.email}</p>
                              </div>
                              <div className="pt-3 border-t border-slate-200">
                                <p className="text-lg font-bold text-[#1e3a5f]">
                                  Gesamtpreis: {COURSE_PRICE.toFixed(2).replace('.', ',')} €
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Payment Method Selection */}
                          <div className="space-y-3 pt-4 border-t">
                            <Label className="text-base font-semibold text-[#1e3a5f]">Zahlungsart wählen *</Label>
                            
                            {/* Selbstzahler-Optionen: PayPal, Bar - nur wenn KEIN BG-Paket */}
                            {selectedPackage && !selectedPackage.isBG && (
                              <>
                                {/* PayPal Option */}
                                <div 
                                  className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                                    paymentMethod === 'paypal' 
                                      ? 'border-blue-500 bg-blue-50' 
                                      : 'border-slate-200 hover:border-slate-300'
                                  } ${!selectedDate || !formData.firstName || !formData.lastName || !formData.email || !formData.privacy ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  onClick={() => {
                                    if (!selectedDate || !formData.firstName || !formData.lastName || !formData.email || !formData.privacy) {
                                      toast.warning('Bitte füllen Sie zuerst alle Pflichtfelder aus.');
                                      return;
                                    }
                                    setPaymentMethod('paypal');
                                  }}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                      paymentMethod === 'paypal' ? 'border-blue-500' : 'border-slate-300'
                                    }`}>
                                      {paymentMethod === 'paypal' && <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />}
                                    </div>
                                    <CreditCard className="w-5 h-5 text-blue-600" />
                                    <div className="flex-1">
                                      <p className="font-medium text-sm">PayPal</p>
                                      <p className="text-xs text-slate-500">Sofortige Zahlung – {COURSE_PRICE.toFixed(2).replace('.', ',')} €</p>
                                    </div>
                                  </div>
                                </div>

                                {/* PayPal API Integration - Vollständige Zahlung */}
                                {paymentMethod === 'paypal' && selectedDate && formData.firstName && formData.lastName && formData.email && formData.privacy && (
                                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                    <p className="text-sm text-slate-600 mb-3 text-center">
                                      Nach Klick werden Sie sicher zu PayPal weitergeleitet.
                                    </p>
                                    <button
                                      onClick={handlePayPalPayment}
                                      disabled={isSubmitting}
                                      className="w-full h-12 bg-[#0070ba] hover:bg-[#003087] text-white font-bold text-base rounded-md flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {isSubmitting ? (
                                        <>
                                          <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                          Wird verarbeitet...
                                        </>
                                      ) : (
                                        <>
                                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797H9.146c-.535 0-.988.394-1.072.928l-1 6.078z"/>
                                          </svg>
                                          Mit PayPal zahlen – {COURSE_PRICE.toFixed(2).replace('.', ',')} €
                                        </>
                                      )}
                                    </button>
                                    <p className="text-xs text-slate-500 mt-2 text-center">
                                      Ihre Buchung wird erstellt und Sie werden zur sicheren PayPal-Zahlung weitergeleitet.
                                      Nach erfolgreicher Zahlung erhalten Sie automatisch eine Bestätigungs-E-Mail.
                                    </p>
                                    <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-center">
                                      <p className="text-xs text-amber-700">
                                        <strong>Hinweis:</strong> Bei Zahlung per PayPal ist eine Stornierung oder Umbuchung ausgeschlossen.
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* Cash Option */}
                                <div 
                                  className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                                    paymentMethod === 'cash' 
                                      ? 'border-blue-500 bg-blue-50' 
                                      : 'border-slate-200 hover:border-slate-300'
                                  }`}
                                  onClick={() => { setPaymentMethod('cash'); setShowPayPal(false); }}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                      paymentMethod === 'cash' ? 'border-blue-500' : 'border-slate-300'
                                    }`}>
                                      {paymentMethod === 'cash' && <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />}
                                    </div>
                                    <Banknote className="w-5 h-5 text-green-600" />
                                    <div className="flex-1">
                                      <p className="font-medium text-sm">Barzahlung vor Ort</p>
                                      <p className="text-xs text-slate-500">Bezahlung am Kurstag – {selectedPackage.price.toFixed(2).replace('.', ',')} €</p>
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}

                            {/* BG-Kostenübernahme Option - nur bei BG-Paket, automatisch ausgewählt und nicht abwählbar */}
                            {selectedPackage && selectedPackage.isBG && (
                              <>
                                <div 
                                  className="border-2 rounded-lg p-3 border-green-500 bg-green-50"
                                  data-testid="payment-bg"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-5 h-5 rounded-full border-2 border-green-500 flex items-center justify-center">
                                      <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                                    </div>
                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                    <div className="flex-1">
                                      <p className="font-medium text-sm text-green-700">BG-Kostenübernahme (kostenfrei)</p>
                                      <p className="text-xs text-slate-500">Abrechnung über Berufsgenossenschaft – 0,00 €</p>
                                    </div>
                                  </div>
                                </div>

                                {/* BG Info Box */}
                                <div className="mt-3 p-4 bg-green-50 rounded-lg border border-green-200">
                                  <div className="flex items-start gap-3">
                                    <AlertCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <p className="font-medium text-green-800 text-sm mb-2">Wichtig für BG-Teilnehmer:</p>
                                      <p className="text-sm text-green-700">
                                        Bitte bringen Sie am Kurstag das <strong>ausgefüllte, gestempelte und unterschriebene DGUV-Abrechnungsformular</strong> mit.
                                      </p>
                                      <a 
                                        href="/betriebliche-erste-hilfe" 
                                        className="inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-800 underline mt-2"
                                      >
                                        <Info className="w-4 h-4" />
                                        Zur Formular-Übersicht & Download
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}

                            {/* Hinweis wenn kein Paket ausgewählt */}
                            {!selectedPackage && (
                              <div className="text-sm text-slate-500 italic">
                                Bitte wählen Sie zuerst ein Paket aus.
                              </div>
                            )}
                          </div>

                          {/* Submit Button (for non-PayPal methods) */}
                          {paymentMethod && paymentMethod !== 'paypal' && selectedPackage && (
                            <Button 
                              type="submit" 
                              className={`w-full h-12 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
                                paymentMethod === 'bg' 
                                  ? 'bg-green-600 hover:bg-green-700' 
                                  : 'bg-red-600 hover:bg-red-700'
                              }`}
                              disabled={!selectedDate || selectedDate.spots_left <= 0 || !paymentMethod || !formData.privacy || isSubmitting}
                            >
                              {isSubmitting ? 'Wird verarbeitet...' : (
                                paymentMethod === 'bg' 
                                  ? 'Verbindlich anmelden (BG-Kostenübernahme)'
                                  : `Verbindlich anmelden – ${COURSE_PRICE.toFixed(2).replace('.', ',')} €`
                              )}
                            </Button>
                          )}
                        </form>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}
            </div>
          </section>

        </main>
        <Footer />
      </div>
  );
};

export default TerminePage;
